import React from 'react';
import { PLATFORM_METRICS } from '~/components/ContentDetailMetrics/ContentDetailMetrics.constants';
import { PopoverGlossary } from '~/components/ContentDetailMetrics/components/PopoverGlossary';
import { ALL_SOCIAL_PLATFORM_TYPE, SOCIAL_PLATFORMS } from '~/constants';
import { trackEvent } from '~/core/utils/trackEvent';
import {
  IApiSearchContent,
  IApiSearchInstagramContent,
  IApiSearchTiktokContent,
  IApiSearchYoutubeContent,
  ISearchContentResult,
} from '~/searchApi/content';
import { CONTENT_TYPE } from '~/types';

import { formatPlatformName, formatProductType } from '../utils/formatters';
import {
  MetricItem,
  getMetricIcon,
  getMetricLabel,
  getPlatformMetrics,
} from '../utils/metricsUtils';

interface MetricsSectionProps {
  platform: ALL_SOCIAL_PLATFORM_TYPE;
  contentData?: IApiSearchContent;
}

export const MetricsSection: React.FC<MetricsSectionProps> = ({
  platform,
  contentData,
}) => {
  const { engagementMetrics, otherMetrics } = getPlatformMetrics(contentData);
  const isOffPlatform = contentData?.creator?.status === 'off-platform';

  // Enhanced PopoverGlossary with tracking
  const PopoverGlossaryWithTracking: React.FC<{
    metrics: any;
    content: ISearchContentResult;
    onlyIcon: boolean;
  }> = ({ metrics, content, onlyIcon }) => {
    const handleGlossaryOpen = () => {
      // Track metrics glossary opens
      trackEvent('foam-tta-metrics-glossary-open', {
        platform,
        contentId: contentData?.id || 'unknown',
        contentType: formatProductType(contentData),
        hasEngagements: (contentData?.engagements ?? 0) > 0,
        isOffPlatform,
      });
    };

    return (
      <div onClick={handleGlossaryOpen}>
        <PopoverGlossary
          metrics={metrics}
          contentData={content}
          onlyIcon={onlyIcon}
        />
      </div>
    );
  };

  // Convert contentData to the appropriate ISearchContentResult type
  const getTypedContent = (): ISearchContentResult | null => {
    if (!contentData) return null;

    if (platform === SOCIAL_PLATFORMS.instagram) {
      return contentData as IApiSearchInstagramContent;
    }

    if (platform === SOCIAL_PLATFORMS.tiktok) {
      return contentData as IApiSearchTiktokContent;
    }

    if (platform === SOCIAL_PLATFORMS.youtube) {
      return contentData as IApiSearchYoutubeContent;
    }

    return null;
  };

  // Get the appropriate metrics configuration for PopoverGlossary
  const getMetricsConfig = () => {
    if (!contentData) return {};

    if (platform === SOCIAL_PLATFORMS.instagram) {
      // For Instagram, use the channel type (post, story, reels)
      const productType = (contentData as IApiSearchInstagramContent)
        .productType;
      if (productType === 'post')
        return PLATFORM_METRICS[CONTENT_TYPE.Post] || {};
      if (productType === 'story')
        return PLATFORM_METRICS[CONTENT_TYPE.Story] || {};
      if (productType === 'reels')
        return PLATFORM_METRICS[CONTENT_TYPE.Reels] || {};
      return PLATFORM_METRICS[CONTENT_TYPE.Post] || {}; // fallback
    }

    // For TikTok and YouTube, use the platform directly
    if (platform === SOCIAL_PLATFORMS.tiktok) {
      return PLATFORM_METRICS[SOCIAL_PLATFORMS.tiktok] || {};
    }

    if (platform === SOCIAL_PLATFORMS.youtube) {
      return PLATFORM_METRICS[SOCIAL_PLATFORMS.youtube] || {};
    }

    return {};
  };

  return (
    <div className="flex flex-col gap-2">
      <h3 className="flex flex-row gap-1 font-normal text-12 font-500 leading-3 text-neutral-300">
        {formatPlatformName(platform).toUpperCase()}{' '}
        {formatProductType(contentData)} METRICS
        {(() => {
          const typedContent = getTypedContent();
          return (
            typedContent && (
              <PopoverGlossaryWithTracking
                metrics={getMetricsConfig()}
                content={typedContent}
                onlyIcon={true}
              />
            )
          );
        })()}
      </h3>

      {!isOffPlatform ? (
        contentData?.engagements && contentData?.engagements > 0 ? (
          <div className="grid grid-cols-1 gap-4 mac:grid-cols-2">
            {/* First column - Engagement metrics */}
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-2">
                <div className="flex w-full items-center justify-between gap-2 px-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="text-neutral-300">
                      {getMetricIcon('engagements')}
                    </span>
                    <span className="truncate text-16 font-400 text-neutral-400">
                      {getMetricLabel('engagements')}
                    </span>
                  </div>
                  <span className="min-w-[40px] text-right text-16 font-500 text-neutral-500">
                    {contentData?.engagements}
                  </span>
                </div>
              </div>
              <div className="ml-2 flex flex-col gap-2 border-l border-neutral-200 pl-3">
                {engagementMetrics.map((metric: MetricItem, index: number) => (
                  <div
                    key={index}
                    className="flex w-full items-center justify-between gap-2 px-1"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="text-neutral-300">{metric.icon}</span>
                      <span className="truncate text-16 font-400 text-neutral-400">
                        {metric.label}
                      </span>
                    </div>
                    <span className="min-w-[40px] text-right text-16 font-500 text-neutral-500">
                      {metric.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Second column - Other metrics */}
            <div className="flex flex-col gap-2">
              {otherMetrics.map((metric: MetricItem, index: number) => (
                <div
                  key={index}
                  className="flex w-full items-center justify-between gap-2 px-1"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="text-neutral-300">{metric.icon}</span>
                    <span className="truncate text-16 font-400 text-neutral-400">
                      {metric.label}
                    </span>
                  </div>
                  <span className="min-w-[40px] text-right text-16 font-500 text-neutral-500">
                    {metric.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 mac:grid-cols-2">
            {/* First column - Other metrics */}
            <div className="flex flex-col gap-2">
              {otherMetrics.map((metric: MetricItem, index: number) => (
                <div
                  key={index}
                  className="flex w-full items-center justify-between gap-2 px-1"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="text-neutral-300">{metric.icon}</span>
                    <span className="truncate text-16 font-400 text-neutral-400">
                      {metric.label}
                    </span>
                  </div>
                  <span className="min-w-[40px] text-right text-16 font-500 text-neutral-500">
                    {metric.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 gap-4 mac:grid-cols-2">
          {/* First column - Engagement metrics */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2">
              {engagementMetrics.map((metric: MetricItem, index: number) => (
                <div
                  key={index}
                  className="flex w-full items-center justify-between gap-2 px-1"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="text-neutral-300">{metric.icon}</span>
                    <span className="truncate text-16 font-400 text-neutral-400">
                      {metric.label}
                    </span>
                  </div>
                  <span className="min-w-[40px] text-right text-16 font-500 text-neutral-500">
                    {metric.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Second column - Other metrics */}
          <div className="flex flex-col gap-2">
            {otherMetrics.map((metric: MetricItem, index: number) => (
              <div
                key={index}
                className="flex w-full items-center justify-between gap-2 px-1"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span className="text-neutral-300">{metric.icon}</span>
                  <span className="truncate text-16 font-400 text-neutral-400">
                    {metric.label}
                  </span>
                </div>
                <span className="min-w-[40px] text-right text-16 font-500 text-neutral-500">
                  {metric.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
